import { useEffect, useMemo, useState } from "react";
import { FaEye, FaEyeSlash } from "react-icons/fa6";
import { FormikProps, withFormik } from "formik";
import { string, object, date, ref } from "yup"; // Add `ref` for confirmPassword validation
import { Input } from "../shared/Input";
import { Button } from "../shared/Button";

export interface ILoginProps {
  onSubmit: (values: any, actions: any) => void;
  toggleForm: () => void;
}

interface FormikValues {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
  role: number;
  dateOfBirth: string;
}

function SignUp(props: FormikProps<FormikValues> & ILoginProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const scrollToFirstError = () => {
    const firstErrorField = Object.keys(props.errors).find(
      (field) => props.errors[field as keyof FormikValues] // Type assertion to access the errors correctly
    );
    if (firstErrorField) {
      const errorElement = document.querySelector(
        `[name="${firstErrorField}"]`
      );
      if (errorElement) {
        // Scroll smoothly to the error field
        errorElement.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }
  };

  useEffect(() => {
    if (Object.keys(props.errors).length > 0) {
      scrollToFirstError();
    }
  }, [Object.keys(props.errors).length]);
  return (
    <form
      onSubmit={async (e) => {
        console.log(props?.errors);
        e.preventDefault();
        props.handleSubmit();
        if (Object.keys(props.errors).length > 0) {
          scrollToFirstError();
        }
      }}
      className="form-container scrollbar-hide"
    >
      <div className="bg-white rounded-lg w-full p-3 md:w-[350px] scrollbar-hide">
        <p className="text-xs text-gray-600 mb-4 text-justify">
          Sign up to get started and enjoy exclusive features!
        </p>

        <div className="space-y-3 scrollbar-hide">
          <div className="lg:flex gap-2">
            <Input
              label="First Name"
              name="firstName"
              values={props.values}
              errors={props.errors}
              onChange={props.handleChange}
              onBlur={props.handleBlur}
              touches={props.touched}
              placeholder="Enter your first Name"
              className="block w-full px-2 mt-2 py-2 border text-xs border-[#b0afb3] rounded-md focus:border-[#b0afb3] focus:outline-none"
            />
            <Input
              label="Last Name"
              name="lastName"
              values={props.values}
              errors={props.errors}
              onChange={props.handleChange}
              onBlur={props.handleBlur}
              touches={props.touched}
              placeholder="Enter your last Name"
              className="block w-full px-2 mt-2 py-2 border text-xs border-[#b0afb3] rounded-md focus:border-[#b0afb3] focus:outline-none"
            />
          </div>

          <Input
            label="Date of Birth"
            name="dateOfBirth"
            values={props.values}
            errors={props.errors}
            onChange={props.handleChange}
            onBlur={props.handleBlur}
            touches={props.touched}
            placeholder="Enter your Date of Birth"
            type="date"
            className="block w-full px-2 mt-2 py-2 border text-xs border-[#b0afb3] rounded-md focus:border-[#b0afb3] focus:outline-none"
          />
          <Input
            label="Email"
            name="email"
            values={props.values}
            errors={props.errors}
            onChange={props.handleChange}
            onBlur={props.handleBlur}
            touches={props.touched}
            placeholder="Enter your Email"
            className="block w-full px-2 mt-2 py-2 border text-xs border-[#b0afb3] rounded-md focus:border-[#b0afb3] focus:outline-none"
          />
          <div className="relative">
            <Input
              label="Password"
              name="password"
              values={props.values}
              errors={props.errors}
              onChange={props.handleChange}
              onBlur={props.handleBlur}
              touches={props.touched}
              placeholder="Enter your Password"
              className="block w-full px-2 mt-2 py-2 border text-xs border-[#b0afb3] rounded-md focus:border-[#b0afb3] focus:outline-none"
              type={showPassword ? "text" : "password"}
            />
            <span
              className="absolute right-3 top-[42px] transform cursor-pointer text-gray-500"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? <FaEye /> : <FaEyeSlash />}
            </span>
          </div>
          <div className="relative">
            <Input
              label="Confirm Password"
              name="confirmPassword"
              values={props.values}
              errors={props.errors}
              onChange={props.handleChange}
              onBlur={props.handleBlur}
              touches={props.touched}
              placeholder="Enter your Confirm Password"
              className="block w-full px-2 mt-2 py-2 border text-xs border-[#b0afb3] rounded-md focus:border-[#b0afb3] focus:outline-none"
              type={showConfirmPassword ? "text" : "password"}
            />
            <span
              className="absolute right-3 top-[42px] transform  cursor-pointer text-gray-500"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            >
              {showConfirmPassword ? <FaEye /> : <FaEyeSlash />}
            </span>
          </div>

          <Button
            className="bg-[#00B207] hover:opacity-70 rounded-full text-white py-2 w-full mt-4 hover:scale-105 duration-300"
            name={props.isSubmitting ? "Loading In..." : "SIGN UP"}
            type="submit"
            loading={props.isSubmitting}
            disabled={props.isSubmitting}
          />
        </div>
        <div className="mt-6 text-gray-400 pb-4">
          <p className="text-left text-sm">
            Already have an account?
            <span
              className="text-green-500 underline ml-1 cursor-pointer"
              onClick={props.toggleForm}
            >
              Sign In
            </span>
          </p>
        </div>
      </div>
    </form>
  );
}

export default withFormik<ILoginProps, FormikValues>({
  validationSchema: object().shape({
    firstName: string().required("First name is required"),
    lastName: string().required("Last name is required"),
    email: string().email("Invalid email format").required("Email is required"),
    password: string()
      .required("Password is required")
      .min(8, "Password must be at least 8 characters")
      .matches(/[A-Z]/, "Password must contain at least one uppercase letter")
      .matches(/[a-z]/, "Password must contain at least one lowercase letter")
      .matches(/[0-9]/, "Password must contain at least one digit")
      .matches(
        /[!@#$%^&*(),.?":{}|<>]/,
        "Password must contain at least one special character"
      ),
    confirmPassword: string()
      .required("Confirm Password is required")
      .oneOf([ref("password")], "Passwords must match"),
    dateOfBirth: date()
      .required("Date of birth is required")
      .max(new Date(), "Date of birth cannot be in the future"),
  }),
  mapPropsToValues: () => ({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
    role: 2,
    dateOfBirth: "",
  }),
  handleSubmit: (values, { props, ...actions }) => {
    props.onSubmit(values, actions);
  },
})(SignUp);
